import React, { useState, useEffect, useMemo } from 'react';
import {
  X, Search, CheckCircle2, Circle, FolderOpen, FolderClosed,
  Download, FileArchive, RefreshCw, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';

/**
 * BackupCustomizeModal
 * ---------------------
 * Lets the admin choose exactly which MongoDB collections (grouped by
 * business domain) go into the next backup, and pick whether to include
 * uploads (PDFs/images) or only the database.
 *
 * The chosen subset persists in localStorage so re-opening the dialog
 * restores the previous selection — handy for daily-routine backups.
 */
const STORAGE_KEY = 'backup_customize_selection_v1';

const BackupCustomizeModal = ({ open, onClose, onBackupComplete }) => {
  const [loading, setLoading]   = useState(false);
  const [running, setRunning]   = useState(false);
  const [catalog, setCatalog]   = useState(null);
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [includeUploads, setIncludeUploads] = useState(true);

  // Fetch the categorised catalog when the dialog opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const r = await axiosInstance.get('/backup/collections');
        if (cancelled) return;
        setCatalog(r.data);
        // Default: expand the first 3 categories
        setExpanded(new Set(
          (r.data.categories || []).slice(0, 3).map(c => c.key),
        ));
        // Restore previous selection from localStorage (if any)
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          try {
            const arr = JSON.parse(saved);
            setSelected(new Set(arr));
          } catch { /* ignore */ }
        } else {
          // First-time: pre-select every collection that has data
          const allWithData = new Set();
          (r.data.categories || []).forEach(cat => {
            cat.collections.forEach(c => {
              if (c.doc_count > 0) allWithData.add(c.id);
            });
          });
          setSelected(allWithData);
        }
      } catch (e) {
        toast.error('فشل في جلب قائمة الصفحات');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Persist selection
  useEffect(() => {
    if (open && selected.size > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...selected]));
    }
  }, [selected, open]);

  // Filter the tree by search query
  const filteredCategories = useMemo(() => {
    if (!catalog?.categories) return [];
    const q = search.trim().toLowerCase();
    if (!q) return catalog.categories;
    return catalog.categories
      .map(cat => ({
        ...cat,
        collections: cat.collections.filter(c =>
          c.id.toLowerCase().includes(q)
          || (c.ar_name || '').toLowerCase().includes(q)
        ),
      }))
      .filter(cat => cat.collections.length > 0);
  }, [catalog, search]);

  // Selection aggregates
  const stats = useMemo(() => {
    let total = 0;
    let docs  = 0;
    let totalDocs = 0;
    (catalog?.categories || []).forEach(cat => {
      cat.collections.forEach(c => {
        total += 1;
        totalDocs += c.doc_count || 0;
        if (selected.has(c.id)) docs += c.doc_count || 0;
      });
    });
    return { totalCollections: total, selectedCount: selected.size,
              selectedDocs: docs, totalDocs };
  }, [catalog, selected]);

  const toggle = (cid) => {
    const next = new Set(selected);
    if (next.has(cid)) next.delete(cid);
    else next.add(cid);
    setSelected(next);
  };

  const toggleCategory = (cat, checked) => {
    const next = new Set(selected);
    cat.collections.forEach(c => {
      if (checked) next.add(c.id);
      else next.delete(c.id);
    });
    setSelected(next);
  };

  const toggleExpanded = (key) => {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setExpanded(next);
  };

  const selectAll = () => {
    const all = new Set();
    (catalog?.categories || []).forEach(cat => {
      cat.collections.forEach(c => all.add(c.id));
    });
    setSelected(all);
  };

  const selectNonEmpty = () => {
    const all = new Set();
    (catalog?.categories || []).forEach(cat => {
      cat.collections.forEach(c => {
        if (c.doc_count > 0) all.add(c.id);
      });
    });
    setSelected(all);
  };

  const deselectAll = () => setSelected(new Set());

  const downloadDirect = async () => {
    if (selected.size === 0) {
      toast.error('يجب اختيار صفحة واحدة على الأقل');
      return;
    }
    try {
      setRunning(true);
      toast.info('جاري تحضير النسخة المخصصة...');
      const collectionsList = [...selected];
      const response = await axiosInstance.post(
        '/backup/download-full-stream',
        { collections: collectionsList, include_uploads: includeUploads },
        { responseType: 'blob', timeout: 600000 },
      );
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/zip' })
      );
      const link = document.createElement('a');
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.href = url;
      link.setAttribute('download', `custom_backup_${ts}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`تم تنزيل نسخة مخصصة (${selected.size} صفحة)`);
      onBackupComplete?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل التنزيل');
    } finally {
      setRunning(false);
    }
  };

  const createServer = async () => {
    if (selected.size === 0) {
      toast.error('يجب اختيار صفحة واحدة على الأقل');
      return;
    }
    try {
      setRunning(true);
      await axiosInstance.post('/backup/create', {
        collections: [...selected],
        include_uploads: includeUploads,
      });
      toast.success('بدأ إنشاء النسخة المخصصة في الخلفية');
      onBackupComplete?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل البدء');
    } finally {
      setRunning(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="backup-customize-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              تخصيص النسخة الاحتياطية
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              اختر الصفحات التي تريد حفظ بياناتها فقط
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg"
            data-testid="customize-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ابحث عن صفحة..."
              className="w-full pr-9 pl-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
              data-testid="customize-search-input"
            />
          </div>
          <button
            onClick={selectNonEmpty}
            className="px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100"
            data-testid="customize-select-nonempty-btn"
          >
            تحديد التي بها بيانات
          </button>
          <button
            onClick={selectAll}
            className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            data-testid="customize-select-all-btn"
          >
            تحديد الكل
          </button>
          <button
            onClick={deselectAll}
            className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            data-testid="customize-deselect-all-btn"
          >
            إلغاء الكل
          </button>
        </div>

        {/* Tree */}
        <div className="flex-1 overflow-y-auto p-3" data-testid="customize-tree">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          )}
          {!loading && filteredCategories.length === 0 && (
            <p className="text-center text-gray-500 py-12">
              لا توجد نتائج مطابقة
            </p>
          )}
          {!loading && filteredCategories.map((cat) => {
            const selectedInCat = cat.collections.filter(c => selected.has(c.id)).length;
            const total = cat.collections.length;
            const allSelected = selectedInCat === total && total > 0;
            const noneSelected = selectedInCat === 0;
            const isOpen = expanded.has(cat.key) || search.trim() !== '';
            return (
              <div
                key={cat.key}
                className="mb-2 border border-gray-100 rounded-xl overflow-hidden"
                data-testid={`category-${cat.key}`}
              >
                <div className="flex items-center bg-gray-50 px-3 py-2.5">
                  <button
                    onClick={() => toggleExpanded(cat.key)}
                    className="p-1 hover:bg-gray-200 rounded mr-1"
                  >
                    {isOpen ? (
                      <FolderOpen className="w-4 h-4 text-amber-500" />
                    ) : (
                      <FolderClosed className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                  <button
                    onClick={() => toggleCategory(cat, !allSelected)}
                    className="flex items-center gap-2 flex-1 text-right"
                    data-testid={`category-toggle-${cat.key}`}
                  >
                    {allSelected ? (
                      <CheckCircle2 className="w-5 h-5 text-blue-600" />
                    ) : noneSelected ? (
                      <Circle className="w-5 h-5 text-gray-300" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-blue-600 bg-blue-100 flex items-center justify-center">
                        <div className="w-2 h-0.5 bg-blue-600" />
                      </div>
                    )}
                    <span className="font-semibold text-gray-800">{cat.ar_name}</span>
                    <span className="text-xs text-gray-500">
                      ({selectedInCat}/{total})
                    </span>
                    {cat.doc_count_total > 0 && (
                      <span className="text-xs text-blue-600 mr-auto">
                        {cat.doc_count_total.toLocaleString('ar-EG')} سجل
                      </span>
                    )}
                  </button>
                </div>
                {isOpen && (
                  <div className="divide-y divide-gray-50">
                    {cat.collections.map((leaf) => {
                      const isSel = selected.has(leaf.id);
                      return (
                        <button
                          key={leaf.id}
                          onClick={() => toggle(leaf.id)}
                          className={`w-full flex items-center gap-3 px-5 py-2 text-right hover:bg-blue-50/30 ${
                            isSel ? 'bg-blue-50/50' : ''
                          } ${!leaf.exists ? 'opacity-40' : ''}`}
                          data-testid={`leaf-toggle-${leaf.id}`}
                        >
                          {isSel ? (
                            <CheckCircle2 className="w-4 h-4 text-blue-600" />
                          ) : (
                            <Circle className="w-4 h-4 text-gray-300" />
                          )}
                          <span className="text-sm text-gray-700 flex-1">
                            {leaf.ar_name}
                          </span>
                          <span className="text-[10px] text-gray-400 font-mono">
                            {leaf.id}
                          </span>
                          <span className={`text-xs ${
                            leaf.doc_count > 0 ? 'text-gray-600' : 'text-gray-300'
                          }`}>
                            {leaf.doc_count.toLocaleString('ar-EG')}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary + Options */}
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
          <div className="flex items-center gap-4 flex-wrap text-sm">
            <span className="text-gray-600">
              تم تحديد <strong className="text-blue-700">{stats.selectedCount}</strong>
              {' '}من {stats.totalCollections} صفحة
            </span>
            <span className="text-gray-400">·</span>
            <span className="text-gray-600">
              <strong className="text-emerald-700">
                {stats.selectedDocs.toLocaleString('ar-EG')}
              </strong>{' '}سجل
            </span>
            <label className="flex items-center gap-2 cursor-pointer ml-auto" data-testid="include-uploads-toggle">
              <input
                type="checkbox"
                checked={includeUploads}
                onChange={(e) => setIncludeUploads(e.target.checked)}
                className="w-4 h-4 accent-blue-600"
              />
              <span className="text-gray-700">شامل الملفات المرفقة (PDF، صور)</span>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="px-5 py-4 border-t border-gray-100 flex gap-2 justify-end">
          <button
            onClick={onClose}
            disabled={running}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            data-testid="customize-cancel-btn"
          >
            إلغاء
          </button>
          <button
            onClick={createServer}
            disabled={running || selected.size === 0}
            className="px-4 py-2 bg-white text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-50 flex items-center gap-2"
            data-testid="customize-server-backup-btn"
          >
            <FileArchive className="w-4 h-4" />
            حفظ على الخادم
          </button>
          <button
            onClick={downloadDirect}
            disabled={running || selected.size === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 font-medium"
            data-testid="customize-download-btn"
          >
            {running ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            تنزيل النسخة المخصصة
          </button>
        </div>
        {selected.size === 0 && (
          <div className="px-5 pb-3 -mt-2 flex items-center gap-2 text-xs text-amber-600">
            <AlertCircle className="w-3.5 h-3.5" />
            اختر صفحة واحدة على الأقل قبل المتابعة
          </div>
        )}
      </div>
    </div>
  );
};

export default BackupCustomizeModal;
